import { CombineOperator, CreatePointRequest, DiscoveryLevel, GatedAccessData, GetPointResponse, GetTopicResponse, KeywordSearch, LatLng, SearchIcon, TopLevelMarkerHolder, TopicGateType, TopicSearchResult } from '../types';
import { DEFAULT_ICON_SIZE, ICONS8_KEY, inspirationMapIds } from './constants';
import { Marker, MarkerUtils } from '@twitchaxorg/markerclusterer';
import { externalGet, logWithAmplitude, memoizeAsync, throwHttpError } from './api/api';
import { getTopic, getTopics } from './api/topics-api';

import { MarkerService } from './api/google-api';
import confetti from 'canvas-confetti';
import { getGatedAccessEvents } from './api/gated-access-api';
import { getMainPage } from './page-name';
import topicData from './state/topic-data';

export function makeId(length: number = 10) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;

    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
}

export function dasherize(str: string) {
    return str.replace(/\s+/g, '-').toLowerCase();
}

export function removeElement(arr: any[], el: any) {
    const index = arr.indexOf(el);
    if (index !== -1) {
        arr.splice(index, 1);
    }

    return arr;
}

export function arrayBufferToBase64(buffer: ArrayBuffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}


export function shallowClone<T>(obj: T) {
    return Object.assign({}, obj);
}


export function isDefined<T>(value: T | undefined | null): value is T {
    return (typeof value !== 'undefined') && (value !== null) && value !== undefined;
}

export function isValidString(value: string | undefined | null): value is string {
    return (typeof value !== 'undefined') && (value !== null) && value !== undefined && value.length !== 0 && !Array.isArray(value);
}

export function isEmpty(value: any) {
    if (value === undefined || value === null || value === '') {
        return true;
    }

    return false;
}

export function isFalsy(item: any) {
    if (item === false || isEmpty(item)) {
        return true;
    };

    return false;
}

export function undefinedIfEmpty(s: string | undefined | null) {
    if (s === undefined || s === null || s.length === 0) {
        return undefined;
    }

    return s;
}

export function undefinedIfEmptyOrEqualsOther(s: string | undefined, other: string) {
    if (s === undefined || s === null || s.length === 0 || s === other) {
        return undefined;
    }

    return s;
}

export function cancelDefault (e: Event) {
    e.preventDefault();
    e.stopPropagation();
    return false;
}

export function parseFloatOrUndefined(s: string | null | undefined) {
    const result = parseFloat(s as any);

    if (isNaN(result)) {
        return undefined;
    }

    return result;
}

export function parseIntOrUndefined(s: string | null | undefined) {
    const result = parseInt(s as any);

    if (isNaN(result)) {
        return undefined;
    }

    return result;
}

export function makeArray(list: NodeListOf<Element>) {
    const arr: Element[] = [];
    list.forEach((n) => arr.push(n));

    return arr;
}

export function anyHasClass(elements: HTMLElement[] | undefined, cl: string) {
    const finder = (e: Element) => {
        return e?.classList?.contains(cl) ?? false;
    };

    return elements?.find(finder) !== undefined;
}

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
export function debounce<T extends (...args: any[]) => void>(
    fn: T,
    delay: number,
    atStart = false,
    guarantee = false
): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    let args: Parameters<T>;
    let self: unknown;

    return function debounced(this: unknown, ...incomingArgs: Parameters<T>) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        self = this;
        args = incomingArgs;

        if (timeout !== null && (atStart || guarantee)) {
            return;
        } else if (!atStart) {
            clear();

            timeout = setTimeout(run, delay);
            return;
        }

        timeout = setTimeout(clear, delay);
        fn.apply(self, args);

        function run() {
            clear();
            fn.apply(self, args);
        }

        function clear() {
            if (timeout !== null) {
                clearTimeout(timeout);
                timeout = null;
            }
        }
    };
}

export async function delay(d: number) {
    return await new Promise((resolve) => {
        setTimeout(resolve, d);
    });
}

// Given a string limit, truncates strings which
// exceed this limit with ellipses
export function smartTruncate(str: string, limit: number) {
    return str.length >= limit ? str.slice(0, limit - 3) + '...' : str;
}

export function getFromLocalStorage(str: string): string | null {
    try {
        return localStorage.getItem(str);
    } catch {
        return null;
    }
}

export function setLocalStorage(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {}
}

export function removeLocalStorage(str: string): void {
    try {
        localStorage.removeItem(str);
    } catch {}
}

// Session-storage equivalents — storage access throws in sandboxed iframes / locked-down browsers,
// so every call is best-effort and must never break the caller.
export function getFromSessionStorage(str: string): string | null {
    try {
        return sessionStorage.getItem(str);
    } catch {
        return null;
    }
}

export function setSessionStorage(key: string, value: string): void {
    try {
        sessionStorage.setItem(key, value);
    } catch {}
}

export function removeSessionStorage(str: string): void {
    try {
        sessionStorage.removeItem(str);
    } catch {}
}

export function testJSON(text: string): boolean {
    if (typeof text !== 'string') {
        return false;
    }
    try {
        JSON.parse(text);
        return true;
    } catch (error) {
        return false;
    }
}

export function shouldDisplayAddPointElement(addPointCustomization: boolean | undefined, previewMode? : boolean): boolean {
    return isFalsy(addPointCustomization) ||
        (topicData.isOwner && addPointCustomization === true && !(previewMode === true)) ||
        (topicData.isWriter && addPointCustomization === true && !(previewMode === true));
}

// set favicon from topic category icon
export function setFavicon(icon: string) {
    const link =
        (document.querySelector("link[rel*='icon']") as HTMLLinkElement) ??
        document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = icon;
    document.getElementsByTagName('head')[0].appendChild(link);
}

// get IP address
export async function getIpAddress() {
    try {
        const response = await fetch('https://api.ipify.org?format=json');
        const json = await response.json();
        return json.ip;
    } catch (error) {
        return '';
    }
}

export async function getGatedAccessData(amplitude_callsite: string, topicId: string, topicKey: string | undefined): Promise<GatedAccessData> {
    let accesses = await getGatedAccessEvents(amplitude_callsite, topicId, topicKey);

    // Convert cents to dollars and percentages to decimals
    accesses = accesses.map(access => {
        if (access.transaction_amount != null) {
            access.transaction_amount = access.transaction_amount / 100;
        }
        if (access.transaction_percent_to_creator != null) {
            access.transaction_percent_to_creator = access.transaction_percent_to_creator / 100;
        }
        return access;
    });

    // Track processed invoice IDs to avoid duplicates
    const processedInvoiceIds = new Set<string>();

    // Filter paywall accesses to include only unique invoice IDs
    const paywall_accesses = accesses
        .filter((access) => access.gate_type === TopicGateType.Paywall)
        .filter((access) => {
            const invoiceId = access.transaction_id;
            // If there's no invoice ID, keep it (can't de-duplicate)
            if (!isDefined(undefinedIfEmpty(invoiceId))) {
                return true;
            }

            // If we've seen this invoice ID before, filter it out
            if (processedInvoiceIds.has(invoiceId as string)) {
                return false;
            }

            // Otherwise, add it to our set and keep this access
            processedInvoiceIds.add(invoiceId as string);
            return true;
        });

    const password_accesses = accesses.filter((access) => access.gate_type === TopicGateType.Password);
    const email_accesses = accesses.filter((access) => access.gate_type === TopicGateType.Email);

    // Calculate total earnings using the same unique invoice approach
    const totalEarningsByInvoice = new Map<string, number>();

    accesses
        .filter((event) => event.gate_type === 'paywall' && isDefined(undefinedIfEmpty(event.transaction_id)))
        .forEach((event) => {
            const invoiceId = event.transaction_id as string;
            const amount = (event.transaction_amount ?? 0) * (event.transaction_percent_to_creator ?? 0);

            // Only store the invoice once (in case of duplicates)
            if (!totalEarningsByInvoice.has(invoiceId)) {
                totalEarningsByInvoice.set(invoiceId, amount);
            }
        });

    // Sum up the unique invoice amounts and format to 2 decimal places
    const total_earned = Number(
        Array.from(totalEarningsByInvoice.values())
            .reduce((total, amount) => total + amount, 0)
            .toFixed(2)
    );

    return {
        accesses,
        paywall_accesses,
        password_accesses,
        email_accesses,
        total_earned
    };
}

export function getImageShape(image: string) {
    // get if image is landscape, portrait or square and set this.shape
    if (isDefined(image)) {
        const img = new Image();
        img.src = image ?? '';
        let imageDimension = '';
        img.onload = () => {
            const width = img.width;
            const height = img.height;
            if (width > height) {
                imageDimension = 'landscape';
            } else if (width < height) {
                imageDimension = 'portrait';
            } else {
                imageDimension = 'square';
            }
            return imageDimension;
        };
    }
    return 'square';
}

export async function getImageDimensions(image: string): Promise<{ width: number; height: number }> {
    return await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = reject;
        img.src = image;
    });
}


export async function findIcon(amplitude_callsite: string, term: string, theme?: string): Promise<SearchIcon[]> {
    if (ICONS8_KEY) {
        const response = await fetch(
            `https://search.icons8.com/api/iconsets/v5/search?term=${term}&token=${ICONS8_KEY}&amount=20&platform=${theme ?? ''}&replaceNameWithSynonyms=true`,
            externalGet()
        );
        if (!response.ok) {
            await throwHttpError('An error occurred while listing icons.', response);
        }

        const icons = (await response.json()).icons as SearchIcon[];
        logWithAmplitude(amplitude_callsite, 'Icon Search', { search_text: term });
        return icons;
    } else {
        throw new Error('ICONS8_KEY can not be found');
    }
}

export async function getFirstIcon(amplitude_callsite: string, search: string): Promise<string | undefined> {
    let resultIcons: SearchIcon[] = [];
    for (let i = 0; i < 3; i++) {
        resultIcons = await findIcon(
            amplitude_callsite,
            search,
            'stickers,emoji,poly,3d-fluency,officel'
        );
        if (resultIcons.length > 2) {
            break;
        } else if (i === 0 && search.endsWith('s')) {
            search = search.slice(0, -1);
        } else if (i === 1) {
            const words = search.split(' ');
            search = words[0];
        }
    }
    if (resultIcons.length === 0) {
        return  'https://img.icons8.com/stickers/marker.png';
    }
    return 'https://img.icons8.com/' + resultIcons[0].platform + '/' + resultIcons[0].commonName + '.png';
}

export async function openInNewTab(url: string){
    const win = window.open(url, '_blank');
    if (win != null) {
        win.focus();
    }
}

export function sortLatLngByDistance(
    latLngs: LatLng[],
    center: LatLng
): LatLng[] {
    return latLngs.sort((a, b) => {
        const distanceA = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(a.lat, a.lng),
            new google.maps.LatLng(center.lat, center.lng)
        );
        const distanceB = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(b.lat, b.lng),
            new google.maps.LatLng(center.lat, center.lng)
        );
        return distanceA - distanceB;
    });
}

export function addParameterToURL(
    url: string,
    param: string,
    value: string,
    afterHash: boolean = false,
    replace_existing: boolean = false
) {
    let urlBeforeHash = url;
    let hash = '';
    let hashParams = new URLSearchParams();

    if (url.includes('#')) {
        const urlParts = url.split('#');
        urlBeforeHash = urlParts[0];
        const hashParts = urlParts[1]?.split('?');
        hash = hashParts[0];
        hashParams = new URLSearchParams(hashParts[1]);
    }

    const urlObj = new URL(urlBeforeHash);
    const params = new URLSearchParams(urlObj.search);

    if (afterHash) {
        if (replace_existing && hashParams.has(param)) {
            hashParams.set(param, value);
        } else if (!hashParams.has(param)) {
            hashParams.append(param, value);
        }
        urlObj.hash = hash + '?' + hashParams.toString();
    } else {
        if (replace_existing && params.has(param)) {
            params.set(param, value);
        } else if (!params.has(param)) {
            params.append(param, value);
        }
        urlObj.search = params.toString();
        if (hash) {
            urlObj.hash = hash + (hashParams.toString() ? '?' + hashParams.toString() : '');
        }
    }

    return urlObj.toString();
}


export function removeParameterFromURL(url: string, param: string) {
    let urlBeforeHash = url;
    let hash = '';
    let hashParams = new URLSearchParams();

    if (url.includes('#')) {
        const urlParts = url.split('#');
        urlBeforeHash = urlParts[0];
        const hashParts = urlParts[1]?.split('?');
        hash = hashParts[0];
        hashParams = new URLSearchParams(hashParts[1]);
    }

    const urlObj = new URL(urlBeforeHash);
    const params = new URLSearchParams(urlObj.search);

    // Delete the parameter from both the query parameters and the hash parameters
    params.delete(param);
    hashParams.delete(param);

    // Update the query parameters and the hash of the URL object
    urlObj.search = params.toString();
    if (hash) {
        urlObj.hash = hash + (hashParams.toString() ? '?' + hashParams.toString() : '');
    }

    return urlObj.toString();
}

export function replaceStateWithPageName(newUrl: string, title?: string) {
    const currentPageUrl = window.location.href;

    let pageName = '';
    if (currentPageUrl.includes('#')) {
        pageName = currentPageUrl.split('#')[1]?.split('?')[0] ?? '';
    } else if (isDefined(history.state?.page_name) && history.state.page_name !== '') {
        pageName = history.state.page_name as string;
    }

    // Swallow any errors as some browsers may begin to throw security errors when
    // trying to access the history state, especially in local testing.
    try {
        window.history.replaceState({ page_name: pageName }, title ?? '', newUrl);
    } catch (e) {
        console.error('Error replacing state (swallowing):', e);
    }
}

export function redirectToPage(newUrl: string, view: string) {
    const currentPageUrl = window.location.href;
    const urlParts = currentPageUrl.split('#');
    const baseUrl = urlParts[0];
    const hashAndQuery = urlParts[1]?.split('?');
    const query = hashAndQuery?.[1] ?? '';
    newUrl = `${baseUrl}#${view}?${query}`;

    // remove the view= part of the query parameters
    newUrl = newUrl.replace(/view=[^&]+&?/, '');


    window.location.href = newUrl;
}

export function transformLatLng(latlng: LatLng): { latitude: number; longitude: number } {
    return {
        latitude: latlng.lat,
        longitude: latlng.lng
    };
}

export function setMetaTag(selector: string, attribute: string, value: string) {
    let element = (document as any).querySelector(selector);

    if (!isDefined(element)) {
        element = (document as any).createElement('meta');
        const propertyAttr = (document as any).createAttribute('property');
        const nameAttr = (document as any).createAttribute('name');

        const splitSelectorProperty = selector.split('meta[property="');
        const splitSelectorName = selector.split('meta[name="');

        if (splitSelectorProperty.length > 1) {
            propertyAttr.value = splitSelectorProperty[1].slice(0, -2); // Extract property value from selector
            element.setAttributeNode(propertyAttr);
        } else if (splitSelectorName.length > 1) {
            nameAttr.value = splitSelectorName[1].slice(0, -2); // Extract name value from selector
            element.setAttributeNode(nameAttr);
        } else {
            console.error(`Invalid selector: ${selector}`);
            return;
        }

        (document as any).head.appendChild(element);
    }

    (element)[attribute] = value;
}

export function setLinkTag(selector: string, attribute: string, value: string) {
    let element = (document as any).querySelector(selector);

    if (!isDefined(element)) {
        element = (document as any).createElement('link');
        const relAttr = (document as any).createAttribute('rel');
        relAttr.value = selector.split('link[rel="')[1].slice(0, -2); // Extract rel value from selector
        element.setAttributeNode(relAttr);
        (document as any).head.appendChild(element);
    }

    (element)[attribute] = value;
}

export function setManifest(name: string, startUrl: string, shortName?: string, smallImage?: string, largeImage?: string, themeColor?: string, backgroundColor?: string) {
    const short_name = isDefined(shortName) ? shortName : name.slice(0, 12);
    const content = {
        name,
        short_name,
        'display': 'standalone',
        'icons': [
            {
                'src': smallImage ?? largeImage ?? 'https://img.icons8.com/stickers/marker.png',
                'sizes': '192x192',
                'type': 'image/png',
                'purpose': 'any maskable'
            },
            {
                'src': largeImage ?? smallImage ?? 'https://img.icons8.com/stickers/marker.png',
                'sizes': '512x512',
                'type': 'image/png',
                'purpose': 'any maskable'
            },
        ],
        'theme_color': themeColor ?? '#ffffff',
        'background_color': backgroundColor ?? '#ffffff',
        'start_url': startUrl ?? window.location.href,
    };
    const manifest = document.createElement('link');
    manifest.rel = 'manifest';
    manifest.href = 'data:application/manifest+json,' + encodeURIComponent(JSON.stringify(content));
    document.head.appendChild(manifest);
}

export function validatePhoneNumber(phoneNumber: string) {
    // remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    // add the country code if it's missing
    let number = '';
    if (cleaned.length === 10) {
        number = '+1' + cleaned;
    } else if (cleaned.length === 11) {
        number =  '+' + cleaned;
    }

    // validate the number with a regex
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    if (phoneRegex.test(number)) {
        return number;
    } else {
        getMainPage().errorToastElement.showToastAndThrow('Invalid phone number');
    }
}


// Get Either Marker or AdvancedMarkerElement depending on if a map has a custom background
export function getMarkerElement(map: google.maps.Map | undefined, position: google.maps.LatLng | google.maps.LatLngLiteral | undefined, icon: google.maps.Icon, z_index: number, advanced :boolean = true, custom_content?: any, title?: string, label?: string) : Marker {
    if (!advanced || !MarkerService.AdvancedMarkerElement) {
        let labelObject: google.maps.MarkerLabel | undefined;
        if (label) {
            labelObject = {
                text: String(label),
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '12px',
            };
        }
        return new google.maps.Marker({
            // [ARoney] DO NOT add `map` here: let the marker clusterer handle it.
            position,
            icon,
            title,
            label: labelObject,
            zIndex: z_index
        });
    } else {
        let content = custom_content;
        if (content === undefined) {
            content = document.createElement('div');
            // add class
            content.className = 'icon_holder_div';
            const img = document.createElement('img');
            img.className = 'icon_holder_img';
            img.src = icon.url;
            img.height = icon.scaledSize?.height ?? DEFAULT_ICON_SIZE;
            img.width = icon.scaledSize?.width ?? DEFAULT_ICON_SIZE;
            content = img;
            if (label) {
                // create a div to hold the label and the icon
                const div = document.createElement('div');
                div.style.position = 'relative'; // Set position to relative so absolute positioning of children is relative to this div
                div.style.display = 'flex';
                div.style.alignItems = 'center';
                div.style.justifyContent = 'center';
                div.appendChild(content);
                const labelDiv = document.createElement('span');
                labelDiv.innerText = label;
                labelDiv.style.position = 'absolute'; // Position labelDiv absolutely within the div
                labelDiv.style.top = '50%'; // Center labelDiv vertically
                labelDiv.style.left = '50%'; // Center labelDiv horizontally
                labelDiv.style.transform = 'translate(-50%, -50%)'; // Offset labelDiv by half its width and height to truly center it
                labelDiv.style.fontSize = '12px';
                labelDiv.style.color = 'rgba(255, 255, 255, 0.9)';
                div.appendChild(labelDiv);
                content = div;
            }
            if (icon.anchor) {
                // set the anchor point of the icon
                // I have no idea why /3 works, but it does
                content.style.transform = `translate(${0}px, ${icon.anchor.y/3}px)`;
            }
        }
        return new MarkerService.AdvancedMarkerElement({
            // [ARoney] DO NOT add `map` here: let the marker clusterer handle it.
            position,
            content,
            title,
            zIndex: z_index,
        });
    }
}

// given a list of categories and number of points, create a marker with a circle background and the top three category icons arranged around it
// with the number of points in the center
export function getClusterMarker(map: google.maps.Map | undefined, position: google.maps.LatLng | google.maps.LatLngLiteral | undefined, categories: string[], numPoints: number, z_index: number, advanced: boolean = true): Marker {
    // Define the SVG and encode it
    const svg = window.btoa(`
        <svg fill="${topicData.topic?.cluster_accent_color ?? topicData.backgroundColor}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
            <circle cx="120" cy="120" opacity=".9" r="70" />
            <circle cx="120" cy="120" opacity=".4" r="100" />
            <circle cx="120" cy="120" opacity=".2" r="120" />
        </svg>
    `);

    // start by creating a div to hold the circle background and the icons
    const div = document.createElement('div');
    div.style.position = 'relative';
    div.style.display = 'flex';
    div.style.alignItems = 'center';
    div.style.justifyContent = 'center';
    div.style.width = '40px';
    div.style.height = '40px';
    div.style.borderRadius = '50%';
    div.style.backgroundImage = `url('data:image/svg+xml;base64,${svg}')`;
    div.style.backgroundSize = 'cover';
    div.style.color = 'white';
    div.style.fontSize = '10px';
    div.style.zIndex = '600';

    // create a list of icon elements
    const iconElements: HTMLDivElement[] = [];
    const radius = 10; // radius of the circle, adjusted to pull icons closer
    const centerX = 20; // center x-coordinate (half of the div size)
    const centerY = 20; // center y-coordinate (half of the div size)

    if (categories.length > 1) {
        for (let i = 0; i < Math.min(categories.length, 8); i++) {
            const angle = (i * (360 / Math.min(categories.length, 8))) * (Math.PI / 180) - 90; // distribute icons evenly around the circle
            const iconSize = 15; // progressively smaller icons
            const x = centerX + radius * Math.cos(angle) - iconSize / 2;
            const y = centerY + radius * Math.sin(angle) - iconSize / 2;

            const iconContainer = document.createElement('div');
            iconContainer.style.position = 'absolute';
            iconContainer.style.left = `${x}px`;
            iconContainer.style.top = `${y}px`;
            iconContainer.style.width = `${iconSize}px`;
            iconContainer.style.height = `${iconSize}px`;
            iconContainer.style.borderRadius = '50%';
            iconContainer.style.backgroundColor = 'white';
            iconContainer.style.display = 'flex';
            iconContainer.style.alignItems = 'center';
            iconContainer.style.justifyContent = 'center';
            iconContainer.style.zIndex = `${500 - i}`;
            iconContainer.style.border = `1px solid ${topicData.topic?.cluster_accent_color ?? topicData.backgroundColor}`;

            const icon = document.createElement('img');
            icon.src = categories[i];
            icon.style.width = '60%';
            icon.style.height = '60%';

            iconContainer.appendChild(icon);
            iconElements.push(iconContainer);
        }
    }

    // create a circle div right inthe center in the front with the number of points
    const centerCircle = document.createElement('div');
    centerCircle.style.position = 'absolute';
    centerCircle.style.left = '50%';
    centerCircle.style.top = '50%';
    centerCircle.style.transform = 'translate(-50%, -50%)';
    centerCircle.style.width = '15px';
    centerCircle.style.height = '15px';
    centerCircle.style.borderRadius = '50%';
    centerCircle.style.backgroundColor = topicData.topic?.cluster_accent_color ?? topicData.backgroundColor;
    centerCircle.style.display = 'flex';
    centerCircle.style.alignItems = 'center';
    centerCircle.style.justifyContent = 'center';
    centerCircle.style.zIndex = `501`;
    centerCircle.style.color = `${topicData.textColor}`;
    centerCircle.innerText = String(numPoints);
    centerCircle.style.cursor = 'pointer';

    div.appendChild(centerCircle);

    // add the icons to the div
    iconElements.forEach((icon) => div.appendChild(icon));

    // svg to url
    const url = `data:image/svg+xml;base64,${svg}`;

    // create the marker
    return getMarkerElement(map, position, { url, scaledSize: new google.maps.Size(40, 40), anchor: new google.maps.Point(20, 20) }, z_index, advanced, div, undefined, String(numPoints));
}

export function updateMarkerPosition(marker: Marker, position: google.maps.LatLng | google.maps.LatLngLiteral) {
    if (marker instanceof google.maps.Marker) {
        marker.setPosition(position);
    } else {
        marker.position = position;
    }
}

export function updateMarkerIcon(marker: Marker, icon: google.maps.Icon) {
    if (marker instanceof google.maps.Marker) {
        marker.setIcon(icon);
    } else {
        // find div with class icon_holder_img and update the src
        const contentElement = marker?.content as HTMLElement;

        // Ensure contentElement is not null and contains the expected structure
        if (isDefined(contentElement)) {
            // Use a more specific selector if necessary
            const img = contentElement.classList.contains('icon_holder_img') ? contentElement as HTMLImageElement : contentElement.querySelector('.icon_holder_img') as HTMLImageElement;
            if (isDefined(img)) {
                img.src = icon.url;
            } else {
                console.error('Image element not found');
            }
        } else {
            console.error('Content element is null or undefined');
        }
    }
}

export function updateMarkerTitle(marker_holder: TopLevelMarkerHolder, title: string) {
    if (marker_holder.inner instanceof google.maps.Marker) {
        // nothing
    } else {
        const div = marker_holder.focusDivs?.aMTextBelow as HTMLElement;
        div.innerText = title;
    }
}

export function updateMarkerZIndex(marker: Marker, z_index: number) {
    if (marker instanceof google.maps.Marker) {
        marker.setZIndex(z_index);
    } else {
        marker.zIndex = z_index;
    }
}

export function updateMarkerVisibility(marker: Marker, visible: boolean) {
    if (marker instanceof google.maps.Marker) {
        marker.setVisible(visible);
    } else {
        marker.position = visible ? marker.position : undefined;
    }
}

export function setMarkerMap(marker: Marker, map: google.maps.Map | null) {
    if (marker instanceof google.maps.Marker) {
        marker.setMap(map);
    } else {
        marker.map = map;
    }
}

// for temporarily removing a marker from the map, not deleting it
// use deleteMarker in point-cache.ts to delete a marker
export function removeMarker(marker: Marker) {
    if (marker instanceof google.maps.Marker) {
        marker.setMap(null);
    } else {
        MarkerUtils.setMap(marker, null);
    }
}

export function getMarkerPosition(marker: Marker) : LatLng {
    if (marker instanceof google.maps.Marker) {
        // if latLngLiteral is returned, convert it to a LatLng
        return marker.getPosition()?.toJSON() as LatLng;
    } else {
        return {
            lat: marker.position?.lat as number,
            lng: marker.position?.lng as number
        };
    }
}

export function setMarkerClickable(marker: Marker, clickable: boolean) {
    if (marker instanceof google.maps.Marker) {
        marker.setClickable(clickable);
    } else {
        marker.gmpClickable = clickable;
    }
}

export function setMarkerDraggable(marker: Marker, draggable: boolean) {
    const draggableIcon = {
        url: 'https://img.icons8.com/?size=48&id=81129&format=png',
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 20),
    };
    updateMarkerIcon(marker, draggableIcon);
    if (draggable) {
        if (marker instanceof google.maps.Marker) {
            marker.setDraggable(true);
        } else {
            marker.gmpDraggable = true;
        }
    }
}

export function getMarkerTitle(marker: Marker) {
    if (marker instanceof google.maps.Marker) {
        return marker.getTitle();
    } else {
        return marker.title;
    }
}


export async function getInspirationMaps() {

    const keywordsArray =
        topicData.topic?.categories.flatMap((category) => category.name.toLowerCase().split(' ')) ?? [];

    const inspirationMaps: TopicSearchResult[] = [];

    const seenMapIds = new Set<string>();
    const promises = [CombineOperator.And, CombineOperator.Or].map(async (operator) => {
        const keywordSearch: KeywordSearch = {
            operator,
            keywords: keywordsArray,
            sub_searches: []
        };

        const searchFilter = {
            discovery_level: DiscoveryLevel.ReadPublic,
            minimum_point_count: 15,
            keyword_search: keywordSearch
        };

        const newResults: TopicSearchResult[] = await getTopics('inspiration maps', searchFilter);
        const filteredResults = newResults
            .filter((result) => result.statistics?.approximate_topic_thirty_day_views ?? 0 > 25)
            .filter((result) => !seenMapIds.has(result._id.$oid)) // filter out duplicates
            .slice(0, 10);
        filteredResults.forEach((result) => seenMapIds.add(result._id.$oid)); // add new map IDs to the Set
        inspirationMaps.push(...filteredResults);
    });
    await Promise.all(promises);

    // if there are no results, show the default inspiration maps by picking 7 random ids from the default inspiration maps list and getting those maps
    if (inspirationMaps.length === 0) {
        const randomIds = inspirationMapIds.sort(() => Math.random() - Math.random()).slice(0, 7);
        const promises = randomIds.map(async (id) => {
            const newResults: GetTopicResponse = await getTopic('inspiration maps', id);
            inspirationMaps.push(newResults);
        });
        await Promise.all(promises);
    }
}

export async function waitForElement(element: any): Promise<boolean> {
    const maxWaitTime = 2000; // Maximum wait time of 2 seconds
    const interval = 100; // Check every 100 milliseconds
    let timeElapsed = 0;

    while (timeElapsed < maxWaitTime) {
        if (isDefined(element)) {
            return true;
        }

        await new Promise(resolve => setTimeout(resolve, interval));
        timeElapsed += interval;
    }

    return false;
};

export function numberToPlaceValue(num: number) {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const v = num % 100;
    return `${num}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
}

export function intoPointRequest(point: GetPointResponse): CreatePointRequest {
    return {
        ...point,
        tag_info: point.tag_info ?? { user_tags: [], google_tags: [] },
    };
}

export function intoPointRequests(point: GetPointResponse[]): CreatePointRequest[] {
    return point.map(intoPointRequest);
}

// Takes a URL, downloads the data, and converts it to a Data URL (base64 encoded string).
// This is useful for weird contexts where CORS doesn;t allow certain web requests (
// e.g., inside of an SVG).
async function _urlToDataUrl(url: string): Promise<string | ArrayBuffer | null> {
    const response = await fetch(url);
    if (!response.ok) {
        // error - throw it here we will handle it in the caller
        throw new Error(`Failed to fetch URL: ${url} - Status: ${response.status}`);
    }
    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

export function kilometersOrMiles() : 'km' | 'mi' {
    if (window.navigator.language === 'en-US' || window.navigator.language === 'my') {
        return 'mi';
    } else {
        return 'km';
    }
}

// Takes a URL, downloads the data, and converts it to a Data URL (base64 encoded string).
// This is useful for weird contexts where CORS doesn;t allow certain web requests (
// e.g., inside of an SVG).
export const urlToDataUrl = memoizeAsync(_urlToDataUrl);

// True when this page is being rendered by something that isn't a person —
// a headless/automated browser like the Thum.io OG-screenshot farm. Gate
// analytics and view-counter writes on this so render traffic doesn't
// inflate GA sessions or customer-facing view stats. Keep in sync with the
// gtag bootstrap check in index.html.
export function isHeadlessRenderContext(): boolean {
    return (
        window.navigator.webdriver ||
        /HeadlessChrome|puppeteer|playwright|prerender/i.test(window.navigator.userAgent)
    );
}

export function injectGoogleTagIfConfigured(google_tag?: string) {
    // if no google tag is set, do nothing
    if (!isDefined(google_tag)) {
        return;
    }
    try {
        if (isDefined(google_tag)) {
            // Sanitize the tag to ensure it matches expected format
            const isValidTag = /^(GTM-[A-Z0-9]+|G-[A-Z0-9]+|UA-[0-9]+-[0-9]+)$/.test(google_tag);
            if (!isValidTag) {
                console.error('Invalid Google Tag format:', google_tag);
            } else {
                // Check if the tag is already injected to avoid duplicates
                const existingScript = document.querySelector(`script[src*="${CSS.escape(google_tag)}"]`);
                if (!existingScript) {
                    // Inject Google Tag Manager or GA4 script
                    if (google_tag.startsWith('GTM-')) {
                        // Google Tag Manager
                        const script = document.createElement('script');
                        script.async = true;
                        script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(google_tag)}`;
                        document.head.appendChild(script);

                        // Add dataLayer initialization
                        const dataLayerScript = document.createElement('script');
                        dataLayerScript.textContent = `
                            window.dataLayer = window.dataLayer || [];
                            window.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
                        `;
                        document.head.insertBefore(dataLayerScript, document.head.firstChild);
                    } else if (google_tag.startsWith('G-')  || google_tag.startsWith('UA-')) {
                        // Google Analytics 4 or Universal Analytics
                        const script = document.createElement('script');
                        script.async = true;
                        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(google_tag)}`;
                        document.head.appendChild(script);

                        // Add gtag initialization - use a safe method to pass the tag ID
                        const gtagScript = document.createElement('script');
                        // Create the script content safely without string interpolation
                        const scriptContent = [
                            'window.dataLayer = window.dataLayer || [];',
                            'function gtag(){dataLayer.push(arguments);}',
                            'gtag(\'js\', new Date());',
                            `gtag('config', '${google_tag.replace(/'/g, "\\'")}');`
                        ].join('\n');
                        gtagScript.textContent = scriptContent;
                        document.head.appendChild(gtagScript);
                    }
                }
            }
        }
    } catch (e) {
        console.error('Error injecting Google Tag', e);
    }
}

export function show_confetti(submitted_colors?: string[] | undefined) {
    // Ensure submitted_colors is an array
    if (!Array.isArray(submitted_colors)) {
        submitted_colors = ['ff0000', '00ff00', '0000ff'];
    }

    // make colors all uppercase and make sure they don't have a # in front
    const colorsCaped = submitted_colors.map((color) => color.replace('#', ''));
    // make sure colors are all 6 characters long
    const colors6 = colorsCaped.map((color) => color.length === 6 ? color : 'ff0000');
    // make sure colors are all uppercase
    const colorsUpper = colors6.map((color) => color.toUpperCase());
    void confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: colorsUpper
    });
}